import React, { useState, useEffect } from "react";
import "./BingoCard.css";
import images, { imageMap } from "../common/images";
import { encodeState, decodeState, fyShuffle } from "../common/shareCode";
import {
  BIASES,
  NEW_FIGHTERS,
  weightByTags,
  sampleWithoutReplacementWeighted,
} from "../common/bingoUtils";

function SettingsModal({
  isOpen,
  onClose,
  includeMiiCharacters,
  setIncludeMiiCharacters,
  includeDashFighters,
  setIncludeDashFighters,
  includeNewFighter,
  setIncludeNewFighter,
  biasId,
  setBiasId,
}) {
  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content">
        <h4>設定</h4>
        <label>
          Miiファイターを含める:
          <input
            type="checkbox"
            checked={includeMiiCharacters}
            onChange={() => setIncludeMiiCharacters(!includeMiiCharacters)}
          />
        </label>
        <label>
          ダッシュファイターを含める:
          <input
            type="checkbox"
            checked={includeDashFighters}
            onChange={() => setIncludeDashFighters(!includeDashFighters)}
          />
        </label>
        <label>
          おまかせを含める:
          <input
            type="checkbox"
            checked={includeNewFighter}
            onChange={() => setIncludeNewFighter(!includeNewFighter)}
          />
        </label>
        <label style={{ display: "block", marginTop: 8 }}>
          トッピング:
          <select
            value={biasId}
            onChange={(e) => setBiasId(e.target.value)}
            style={{ marginLeft: 8 }}
          >
            {BIASES.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <button onClick={onClose}>閉じる</button>
      </div>
    </div>
  );
}

function BingoCard3({ color, initialCode, onCodeChange }) {
  const [selectedImages, setSelectedImages] = useState([]);
  const [clicked, setClicked] = useState(Array(49).fill(false));
  const [includeMiiCharacters, setIncludeMiiCharacters] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [includeDashFighters, setIncludeDashFighters] = useState(true);
  const [includeNewFighter, setIncludeNewFighter] = useState(true);
  const [biasId, setBiasId] = useState("none");
  const [lastAppliedBias, setLastAppliedBias] = useState(null);
  const [inputCode, setInputCode] = useState("");

  useEffect(() => {
    if (initialCode) {
      const decodedImages = decodeState(initialCode);
      if (decodedImages.length > 0) {
        setSelectedImages(decodedImages);
        const next = Array(49).fill(false);
        next[24] = true;
        setClicked(next);
        setIsInitialLoad(false);
        return;
      }
    }
    generateBingoCard(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateBingoCard = (showAlert = true) => {
    if (showAlert && !isInitialLoad) {
      if (!window.confirm("新しいビンゴカードを生成しますか？進行中のゲームはリセットされます。")) {
        return;
      }
    }

    let pool = [...images];
    if (!includeMiiCharacters) {
      pool = pool.filter(
        (img) => ![imageMap.miifighter, imageMap.miigunner, imageMap.miiswordsman].includes(img)
      );
    }
    if (!includeDashFighters) {
      pool = pool.filter(
        (img) => ![imageMap.daisy, imageMap.pitb, imageMap.samusd, imageMap.richter].includes(img)
      );
    }
    if (!includeNewFighter && NEW_FIGHTERS.length > 0) {
      pool = pool.filter((img) => !NEW_FIGHTERS.includes(img));
    }

    let bias = BIASES.find((b) => b.id === biasId) ?? BIASES[0];
    if (bias.id === "auto") {
      const candidates = BIASES.filter((b) => b.id !== "none" && b.id !== "auto");
      bias = candidates[Math.floor(Math.random() * candidates.length)];
    }

    const picked = fyShuffle(sampleWithoutReplacementWeighted(pool, (img) => weightByTags(img, bias), 49));
    setSelectedImages(picked);

    const initialClicked = Array(49).fill(false);
    initialClicked[24] = true;
    setClicked(initialClicked);

    setLastAppliedBias(bias);
    onCodeChange?.(encodeState(picked));
    setIsInitialLoad(false);
  };

  const handleCellClick = (index) => {
    const newClicked = [...clicked];
    newClicked[index] = !newClicked[index];
    setClicked(newClicked);
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const reshuffleLayout = () => {
    const clickedImages = new Set(selectedImages.filter((_, i) => clicked[i]));
    const reshuffled = fyShuffle(selectedImages);
    setSelectedImages(reshuffled);
    setClicked(reshuffled.map((img) => clickedImages.has(img)));
  };

  const handleLoadDeck = () => {
    const decodedImages = decodeState(inputCode);
    if (decodedImages.length === 0) return;
    setSelectedImages(decodedImages);
    const next = Array(49).fill(false);
    next[24] = true;
    setClicked(next);
    onCodeChange?.(inputCode);
    setInputCode("");
  };

  return (
    <div className="bingo-container" style={{ borderColor: color }}>
      <div
        style={{ textAlign: "center", marginTop: 6, fontSize: 18, color: "#EA3D25" }}
      >
        トッピング（現在）:{" "}
        {lastAppliedBias?.name ?? (BIASES.find((b) => b.id === biasId)?.name || "なし")}
      </div>
      <div style={{ position: "relative", display: "inline-block" }}>
        <div className={`bingo-card-7x7 ${color}`}>
          {selectedImages.map((image, index) => (
            <div
              key={index}
              className={`bingo-cell bingo-cell-7x7 ${clicked[index] ? "clicked" : ""}`}
              onClick={() => handleCellClick(index)}
              style={{ backgroundImage: `url(${image})` }}
            ></div>
          ))}
        </div>
        <button
          onClick={reshuffleLayout}
          title="並べ替え"
          style={{
            position: "absolute",
            bottom: "4px",
            right: "4px",
            padding: "4px 8px",
            fontSize: "12px",
            background: "#5c5c5c",
            border: "none",
            color: "#ccc",
            borderRadius: "4px",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          🔁 並べ替え
        </button>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "10px",
        }}
      >
        <button onClick={() => generateBingoCard(true)}>ビンゴを作る</button>
        <button onClick={toggleSettings} style={{ marginTop: "10px" }}>
          ビンゴのカスタマイズ
        </button>
        <div style={{ display: "flex", gap: "6px", marginTop: "10px" }}>
          <input
            type="text"
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLoadDeck()}
            placeholder="ビンゴコードを入力"
            style={{ padding: "6px 8px", borderRadius: "4px", border: "1px solid #666", background: "#4a4a4a", color: "#eaeaea", fontSize: "13px", width: "160px" }}
          />
          <button
            onClick={handleLoadDeck}
            style={{ marginTop: "0", padding: "6px 10px", fontSize: "13px" }}
          >
            ロード
          </button>
        </div>
      </div>
      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          onClose={toggleSettings}
          includeMiiCharacters={includeMiiCharacters}
          setIncludeMiiCharacters={setIncludeMiiCharacters}
          includeDashFighters={includeDashFighters}
          setIncludeDashFighters={setIncludeDashFighters}
          includeNewFighter={includeNewFighter}
          setIncludeNewFighter={setIncludeNewFighter}
          biasId={biasId}
          setBiasId={setBiasId}
        />
      )}
    </div>
  );
}

export default BingoCard3;
